import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { GeneratikTableColumn } from '@/shared/ui/GeneratikTable';
import { SortOption } from '@/shared/ui/GeneratikTable/GeneratikTableToolbar.types';

import { PendingPickupRow } from '../../../model/pendingPickups';
import formatPackageCounts from './formatPackageCounts';

const usePendingPickupsColumns = (createdAtById: Record<string, string> = {}) => {
  const { t } = useTranslation();

  const columns = useMemo<GeneratikTableColumn<PendingPickupRow>[]>(
    () => [
      {
        field: 'awbNumber',
        headerName: t('awb.column_awb_number'),
        sortable: true,
        cellType: 'shortText',
        valueGetter: (_value, row) =>
          row.shipment ? (row.shipment.awbNumber ?? t('awb.pending_number')) : '',
      },
      {
        field: 'recipient',
        headerName: t('Recipient'),
        sortable: true,
        cellType: 'text',
        valueGetter: (_value, row) => (row.shipment ? (row.shipment.recipientName ?? '-') : ''),
      },
      {
        field: 'city',
        headerName: t('awb.column_city'),
        sortable: true,
        cellType: 'shortText',
        valueGetter: (_value, row) => (row.shipment ? (row.shipment.recipientLocality ?? '-') : ''),
      },
      {
        field: 'parcels',
        headerName: t('Parcels'),
        sortable: true,
        cellType: 'number',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) =>
          row.shipment ? (row.shipment.parcels ?? 0) + (row.shipment.envelopes ?? 0) : null,
        renderCell: ({ row }) =>
          row.shipment
            ? formatPackageCounts(t, row.shipment.parcels ?? 0, row.shipment.envelopes ?? 0)
            : null,
      },
      {
        field: 'weight',
        headerName: t('awb.tariff_weight'),
        sortable: true,
        cellType: 'number',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value, row) => row.shipment?.weight ?? 0,
        renderCell: ({ row }) =>
          !row.shipment
            ? null
            : row.shipment.weight !== undefined && row.shipment.weight !== null
              ? `${row.shipment.weight} kg`
              : '-',
      },
      {
        field: 'createdAt',
        headerName: t('awb.column_created_at'),
        sortable: true,
        cellType: 'shortText',
        valueGetter: (_value, row) => (row.shipment ? (createdAtById[row.shipment.id] ?? '') : ''),
        renderCell: ({ row, value }) => {
          if (!row.shipment) return null;
          return value ? dayjs(String(value)).format('DD MMM, HH:mm') : '-';
        },
      },
    ],
    [createdAtById, t],
  );

  const sortOptions = useMemo<SortOption[]>(
    () =>
      columns
        .filter((column) => column.sortable)
        .map((column) => ({ field: column.field, label: column.headerName ?? '' })),
    [columns],
  );

  return { columns, sortOptions };
};

export default usePendingPickupsColumns;
